import React, { useState } from 'react';

import { FormattedMessage } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Popup } from '@atlaskit/popup/popup';
import type { ContentProps } from '@atlaskit/popup/types';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { LinksMenuExtensionActions } from '../../../../../controllers/use-links-menu-parameters-and-actions';
import { ITEM, type ItemPositionalProps, type ItemType } from '../../../../../types';
import { IconComponent } from './icon-component';
import { LinksMenuItemConfig } from './links-menu-item-config';
import { LinksMenuItemDropdownMenu } from './links-menu-item-dropdown-menu';
import { messages } from './messages';

const wrapperStyles = xcss({
	height: '32px',
	paddingLeft: 'space.100',
	paddingRight: 'space.050',
});

const unselectedStyles = xcss({
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		borderRadius: 'radius.small',
	},
});

const selectedStyles = xcss({
	borderRadius: 'radius.small',
	background: token('color.background.selected'),
	border: `${token('border.width')} solid ${token('color.border.selected')}`,
});

const entryStyles = xcss({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});

const titleStyles = xcss({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

export const TREE_LEFT_MARGIN: number = 16;
// Config panel is not resizable
const APPROX_BUFFERED_CONFIG_PANEL_WIDTH: number = 255;

type LinksMenuItemProps = ItemPositionalProps & {
	extensionActions: LinksMenuExtensionActions;
	isDragging: boolean;
	item: ItemType;
	numberItems: number;
	parentId?: string;
};

export const LinksMenuItem: React.FC<LinksMenuItemProps> = ({
	extensionActions,
	isFirst,
	isLast,
	index,
	item,
	parentId,
	level,
	numberItems,
	isDragging,
}) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [isConfiguring, setIsConfiguring] = useState(false);
	const [newItemType, setNewItemType] = useState<ITEM | undefined>(undefined);

	const handleClose = () => {
		setIsPopupOpen(false);
		setIsConfiguring(false);
		setNewItemType(undefined);
	};

	const handleMoreButtonClick = () => {
		setIsPopupOpen(!isPopupOpen);
		setIsConfiguring(false);
		setNewItemType(undefined);
	};

	const configProps = newItemType
		? {
				newItemType,
				parentId: item.itemId,
			}
		: {
				existingItem: item,
			};
	const popupAdjustLeftPx = APPROX_BUFFERED_CONFIG_PANEL_WIDTH - TREE_LEFT_MARGIN * level;
	const popupAdjustDownPx = item?.type === ITEM.MENU ? 50 : 80;

	return (
		<Flex
			alignItems="center"
			justifyContent="space-between"
			xcss={[wrapperStyles, isConfiguring ? selectedStyles : unselectedStyles]}
		>
			<Flex alignItems="center" columnGap="space.100" xcss={entryStyles}>
				<IconComponent item={item} />
				<Box xcss={titleStyles} testId={`links-menu-item-title-${index}-${item.itemId}`}>
					{item.title}
				</Box>
			</Flex>
			<Popup
				isOpen={isPopupOpen && !isDragging}
				onClose={handleClose}
				placement="auto"
				// move popup to left of list item when item config is invoked,
				// so user can see what they are editing
				offset={isConfiguring ? [popupAdjustDownPx, popupAdjustLeftPx] : undefined}
				content={({ update }: ContentProps) => {
					if (isConfiguring) {
						return (
							<LinksMenuItemConfig
								extensionActions={extensionActions}
								handleClose={handleClose}
								update={update}
								{...configProps}
							/>
						);
					} else {
						return (
							<LinksMenuItemDropdownMenu
								extensionActions={extensionActions}
								isFirst={isFirst}
								isLast={isLast}
								index={index}
								level={level}
								item={item}
								parentId={parentId}
								numberItems={numberItems}
								setIsPopupOpen={setIsPopupOpen}
								setIsConfiguring={setIsConfiguring}
								setNewItemType={setNewItemType}
							/>
						);
					}
				}}
				trigger={(triggerProps) => (
					<IconButton
						{...triggerProps}
						icon={ShowMoreHorizontalIcon}
						onClick={handleMoreButtonClick}
						appearance="subtle"
						spacing="compact"
						label={<FormattedMessage {...messages.moreButtonLabel} />}
						isSelected={isPopupOpen && !isConfiguring}
					/>
				)}
			/>
		</Flex>
	);
};
